import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, Grid, Input } from '@mui/material';
import { LanguageType, Location } from '@nx-smartmonkey/shared/domain';
import { validateCoordinates } from '@nx-smartmonkey/shared/helpers';
import { InputSize, InputVariant, RoutalComputeInputTextStyles, RoutalPalette, theme } from '@nx-smartmonkey/ui';
import lodash from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import LocationOnIcon from './../assets/svg/LocationOnIcon';

import useErrorMiddleware from '../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../hooks/snackbarMessage/useShowSnackbarMessage';
import { useRetrieveActiveLanguage } from '../hooks/translations/useRetrieveActiveLanguage';
import { getAutocompleteGeocode, getLocationFromCoordinates, getLocationFromId } from '../services/requests';
import Text from './Text';

let sessionToken: string | undefined;

type InputLocationProps = {
  fieldValue?: Location;
  inputProps?: {
    tabIndex?: number;
    autoFocus?: boolean;
    disabled?: boolean;
    size?: InputSize;
    variant?: InputVariant;
    styles?: React.CSSProperties;
    placeholder?: string;
    placeholderIntlId?: string;
    placeholderIntlValues?: Record<string, string | number>;
  };
  id?: string;
  clearable?: boolean;
  menuPlacement?: `bottom` | `top`;
  fullWidth?: boolean;
  emptyText?: string;
  emptyIntlId?: string;
  emptyIntlValues?: Record<string, string | number>;
  searchWithoutProjectId?: boolean;
  focusedBorderColor?: string;
  onBlur?: (...args: any[]) => any;
  onKeyDown?: (...args: any[]) => any;
  onIsLoading?: (isLoading: boolean) => void;
  onUpdate: (location: Location | null) => void;
  optimisticUpdate?: boolean;
};

const InputLocation = ({
  id,
  fieldValue,
  inputProps,
  menuPlacement,
  clearable,
  fullWidth,
  emptyText,
  emptyIntlId,
  emptyIntlValues,
  searchWithoutProjectId = false,
  focusedBorderColor = RoutalPalette.primary00,
  onBlur,
  onKeyDown,
  onUpdate,
  onIsLoading,
  optimisticUpdate,
}: InputLocationProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const [value, setValue] = useState<Location | null>(fieldValue ?? null);
  const [inputValue, setInputValue] = useState(fieldValue?.label ?? ``);
  const [options, setOptions] = useState<Location[]>(fieldValue ? [fieldValue] : []);
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const [inputHover, setInputHover] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: activeLanguage } = useRetrieveActiveLanguage();

  const intl = useIntl();

  useEffect(() => {
    if (fieldValue && value && !lodash.isEqual(fieldValue, value)) {
      setValue(fieldValue);
      setOptions([fieldValue]);
    }
    if (!fieldValue) {
      setValue(null);
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const handleInputBlur = () => {
    setInputFocused(false);
  };
  const handleInputHover = () => {
    setInputHover(true);
  };
  const handleInputHoverLeave = () => {
    setInputHover(false);
  };
  const handleInputFocus = () => {
    setInputFocused(true);
  };

  const manageAxiosError = (error: any) => {
    if (error.response?.status === 401) {
      sessionExpiredLogout();
    } else if (error.response?.status === 429) {
      showSnackbarMessage({
        messageIntlId: `infoerror.highway.user.error.too_many_requests`,
      });
    } else {
      showSnackbarMessage({
        messageIntlId: `infoerror.${error?.response?.data?.messageId ?? `network_error`}`,
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptionsDelayed = useCallback(
    lodash.debounce(async (text, callback) => {
      try {
        const inputValueSplited = text.split(`,`);
        let newOptions;
        if (
          inputValueSplited.length === 2 &&
          validateCoordinates({ lat: Number(inputValueSplited[0]), lng: Number(inputValueSplited[1]) })
        ) {
          const [lat, lng] = inputValueSplited;
          newOptions = await getLocationFromCoordinates([{ lat, lng }], activeLanguage?.locale as LanguageType);
        } else {
          if (!sessionToken) sessionToken = uuidv4();
          newOptions = await getAutocompleteGeocode({
            searchValue: text,
            projectId: !searchWithoutProjectId ? activeProject?.id : undefined,
            sessionToken,
          });
        }
        callback(newOptions);
      } catch (err) {
        manageAxiosError(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    let active = true;
    if (inputValue === `` || inputValue === fieldValue?.label) {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (active) {
      getOptionsDelayed(inputValue, (newOptions: any[]) => {
        setOptions(newOptions);
      });
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputValue, getOptionsDelayed]);

  // Select the content of the empty text
  const processedEmptyText: string | undefined = emptyIntlId
    ? intl.formatMessage({ id: emptyIntlId }, emptyIntlValues)
    : emptyText;

  if (isLoading) {
    return <Text variant="body" intlId="properties.client.mapping" />;
  }

  return (
    <Autocomplete
      id={`input-location${id ? `-${id}` : ``}`}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      getOptionLabel={(option) => (typeof option === `string` ? option : option.label ?? ``)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      blurOnSelect
      onMouseEnter={handleInputHover}
      onMouseLeave={handleInputHoverLeave}
      value={value}
      fullWidth={fullWidth}
      noOptionsText={processedEmptyText}
      onChange={async (event: any, selectedOption: Location | null) => {
        setValue(selectedOption);

        if (selectedOption?.lat && selectedOption?.lng) {
          await onUpdate(selectedOption);
        } else if (selectedOption === null) {
          await onUpdate(null);
        } else if (selectedOption.location_id) {
          if (optimisticUpdate) onUpdate(selectedOption);
          onIsLoading && onIsLoading(true);
          setIsLoading(true);
          try {
            const resp = await getLocationFromId(selectedOption.location_id, sessionToken);
            sessionToken = undefined;
            await onUpdate(Location.create(resp));
          } catch (err) {
            manageAxiosError(err);
          }
          setIsLoading(false);
          onIsLoading && onIsLoading(false);
        }
      }}
      onInputChange={(event: any, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => {
        const computedStyles = RoutalComputeInputTextStyles({
          size: inputProps?.size,
          variant: inputProps?.variant,
          inputFocused,
          inputStyles: inputProps?.styles,
          focusedBorderColor,
          hoverBGColor: RoutalPalette.neutral05,
          hover: inputHover,
          bGColor: `transparent`,
        });

        let processedPlaceholder: string;

        // Select the content of the placeholder
        if (inputProps?.placeholderIntlId) {
          processedPlaceholder = intl.formatMessage(
            { id: inputProps.placeholderIntlId },
            inputProps.placeholderIntlValues
          );
        } else {
          processedPlaceholder = inputProps?.placeholder ?? intl.formatMessage({ id: `none` });
        }

        return (
          <Input
            ref={params.InputProps.ref}
            tabIndex={inputProps?.tabIndex}
            autoFocus={inputProps?.autoFocus}
            type="text"
            disableUnderline
            disabled={inputProps?.disabled}
            fullWidth={fullWidth}
            placeholder={processedPlaceholder}
            style={computedStyles.inputContainer}
            inputProps={{
              ...params.inputProps,
              ...{ style: computedStyles.input },
              id: `location-input${id ? `-${id}` : ``}`,
            }}
            onBlur={() => {
              if (fieldValue && !lodash.isEqual(fieldValue, value)) {
                setValue(fieldValue);
                setOptions([fieldValue]);
              }
              handleInputBlur();
            }}
            onFocus={handleInputFocus}
            endAdornment={
              <>
                {clearable && inputValue ? (
                  <ClearIcon
                    style={{
                      width: `20px`,
                      height: `20px`,
                      marginRight: `4px`,
                      color: `${RoutalPalette.secondary60}`,
                    }}
                    onClick={() => {
                      setValue(null);
                      onUpdate(null);
                      setTimeout(() => {
                        handleInputBlur();
                      }, 1000);
                    }}
                  />
                ) : null}
              </>
            }
            startAdornment={
              inputValue ? (
                <div
                  style={{
                    marginLeft: `6px`,
                    marginTop: `5px`,
                  }}
                >
                  <LocationOnIcon height="20px" width="20px" fill={RoutalPalette.secondary60} />
                </div>
              ) : (
                <div
                  style={{
                    marginLeft: `6px`,
                    marginTop: `5px`,
                  }}
                >
                  <LocationOnIcon height="20px" width="20px" fill={RoutalPalette.secondary00} />
                </div>
              )
            }
          />
        );
      }}
      renderOption={(props, location: Location) => (
        <li {...props}>
          <Grid container alignItems="center" wrap="nowrap">
            {location ? (
              <>
                <Grid
                  item
                  style={{
                    marginRight: theme.spacing(2),
                    marginTop: `5px`,
                  }}
                >
                  <LocationOnIcon height="20px" width="20px" fill={RoutalPalette.secondary60} />
                </Grid>
                <Grid item>
                  <Text variant="body" style={{ cursor: `pointer` }}>
                    {location.label ?? ``}
                  </Text>
                </Grid>
              </>
            ) : (
              <Text variant="body" intlId="no_results" />
            )}
          </Grid>
        </li>
      )}
    />
  );
};

export default InputLocation;
